import $ from "jquery";
import LayerViewModel from "./LayerViewModel";

import type {ILayerView, LayerInfo} from "@esWebAPI/es/model/Layer";
import type {AzureBaseMap} from "@esWebAPI/es/map/module/AzureBaseMap";
import type {GeoMap} from "@esWebAPI/es/model/GeoMap";
import {IEventChannel} from "@esWebAPI/es/model/Models";

export class FeatureViewModel {

    private readonly _geoMapId: number;
    private readonly _eventChannel: IEventChannel;

    private readonly _onReady: JQuery.Deferred<FeatureViewModel> = $.Deferred<FeatureViewModel>();

    private fireOnFeatureViewUpdateEventTimeout: any;
    private fireOnFeatureViewUpdateEventPromise: Promise<void> | undefined;
    private fireOnFeatureViewUpdateEventPromiseResolve: (() => void) | undefined;
    private timestamp: number;
    private layers: LayerViewModel[] = [];
    private layersById: {[layerId: number]: LayerViewModel} = {};

    constructor(geoMapId: number, eventChannel: any) {
        this._geoMapId = geoMapId;
        this._eventChannel = eventChannel;

        let tsDfd = es.api.geoMap.get(geoMapId).then(geoMap => {
            this.timestamp = this.calculateTimestamp(geoMap);
        });

        let lvDfd = es.api.layerView.getByGeoMap(geoMapId).then((layers: ILayerView[]) => {
            return this.loadLayerInfo(layers).then((layersInfo) => {
                layers.forEach((l: ILayerView, i: number) => {
                    const lvm = new LayerViewModel(l, layersInfo[i]);
                    this.layersById[l.id] = lvm;
                    this.layers.push(lvm);
                    lvm.getEventChannel().addListener("onUpdate", this.onUpdateLayer, this);
                });

                this.addListeners();
            });
        });

        Promise.all([tsDfd, lvDfd]).then(() => this._onReady.resolve(this));
    }

    private addListeners() {

        function updateFeatureBounds(edits: any[], currentBounds: number[]) {
            const newBounds = currentBounds.slice();
            for (let i = 0; i < edits.length; i++) {
                const geom = edits[i].action.geom;
                if (geom) {
                    const editBounds = es.api.geom.getBounds(geom);
                    es.util.bounds.union(newBounds, editBounds, newBounds);
                }
            }
            return newBounds;
        }

        es.api.getEventChannel()
            .addListener("onEditFeatures", (edits: any[], overallTimestamp: number) =>{
                this.layers.forEach(layer => {
                    // Reset the timestamp on associated layers - this is recalculated on the server, but since layers may be linked, we set all here
                    const edit = edits.find(function(a) { return a.action.layerViewId == layer.getId(); });
                    if (edit){
                        layer.setFeaturesModified(overallTimestamp);
                    }
                    layer.setBounds(updateFeatureBounds(edits, layer.getLayerInfo().featureInfo.bounds));
                });

                // update the feature counts of any layers that had features that were created or removed
                const layerViewIds: number[] = [];
                const layerMetaIds: number[] = [];
                for (let i = edits.length; i-- > 0;){
                    if (edits[i].action.$type == "Create" || edits[i].action.$type == "Remove") {
                        if (edits[i].action.layerViewId > 0 ) {
                            if (!layerViewIds.includes(edits[i].action.layerViewId)) {
                                layerViewIds.push(edits[i].action.layerViewId);
                            }
                        } else {
                            if (!layerMetaIds.includes(edits[i].action.layerMetaId)) {
                                layerMetaIds.push(edits[i].action.layerMetaId);
                            }
                        }
                    }
                }
                for (let i = layerViewIds.length; i-- > 0;){
                    es.api.layerInfo.getForLayerView(layerViewIds[i]).done((layerInfo) => {
                        this.layers.forEach(layer => {
                            if (layer.getId() === layerInfo.layerViewId) {
                                layer.setFeatureCount(layerInfo.featureInfo.count);
                            }
                        });
                    });
                }

                for (let i = layerMetaIds.length; i-- > 0;){
                    es.api.layerInfo.getForLayerMeta(layerMetaIds[i]).done((layerInfo) => {
                        this.layers.forEach(layer => {
                            if (layer.getLayerMetaId() === layerInfo.layerMetaId) {
                                layer.setFeatureCount(layerInfo.featureInfo.count);
                            }
                        });
                    });
                }

                // Set the overall timestamp
                this.setTimestamp(overallTimestamp);
            }, this)
            .addListener("onRemoveLayerView", (layerViewId: number) => {
                this.removeLayer(layerViewId);
            }, this)
            .addListener("onCreateLayerView", async (layerView: ILayerView, layerInfo: LayerInfo) => {
                if (this._geoMapId === layerView.geoMapId) {
                    await this.addLayerView(layerView, layerInfo);
                }
            }, this)
            .addListener("onBatchEditFeatures", (_numFeatures: number, layerViewId: number, _force: boolean, lastModified: number) => {
                this.layers.forEach(function(layer) {
                    layer.setFeaturesModified(lastModified);
                });
                es.api.layerInfo.getForLayerView(layerViewId).done((layerInfo) =>{
                    const layer = this.layers.find(function(layer) {
                        return layer.getId() === layerInfo.layerViewId;
                    });
                    if (layer) {
                        layer.setFeatureCount(layerInfo.featureInfo.count);
                        layer.setBounds(layerInfo.featureInfo.bounds);
                    }
                    // wait for the new bounds to be set before refreshing
                    this.setTimestamp(lastModified);
                });
            }, this)
            .addListener("onSyncDatasetFinish", (syncDatasetFinishRS: any) => {

                const layerInfoDfds = [];
                const layerViewDfds = [];

                for (let i = 0; i < syncDatasetFinishRS.length; i++){
                    if(syncDatasetFinishRS[i].statusObj["errorMessage"]){
                        throw new Error(syncDatasetFinishRS[i].statusObj["errorMessage"]); // this is a fallback handler. err should be handled earlier (i.e. on server and/or integration module)
                    } else {
                        const associatedLayerView = this.listLayers().find(lvm => {
                            return lvm.getLayerMetaId() == syncDatasetFinishRS[i].statusObj.layerMeta.id; }
                        );
                        if(!associatedLayerView){
                            console.log("Error ");
                            return;
                        }
                        layerInfoDfds.push(es.api.layerInfo.getForLayerView(associatedLayerView.getId(), undefined, {noCache: true}));
                        layerViewDfds.push(es.api.layerView.get(associatedLayerView.getId(), {noCache: true}));
                    }
                }

                Promise.all(layerViewDfds).then((layerViews) => {
                    for (let i = 0; i < layerViews.length; i++) {
                        const layerView = layerViews[i];
                        const layer = this.layers.find(layer =>{
                            return layer.getId() === layerView.id;
                        });
                        if (layer){
                            layer.setImportMeta(layerView.importMeta);
                        }
                    }
                });

                let overallTimestamp = 0;
                Promise.all(layerInfoDfds).then((layerInfos) => {
                    for (let i = 0; i < layerInfos.length; i++) {
                        const layerInfo = layerInfos[i];
                        const layer = this.layers.find(function(layer) {
                            return layer.getLayerMetaId() === layerInfo.layerMetaId;
                        });
                        if (layer) {
                            layer.setFeatureCount(layerInfo.featureInfo.count);
                            layer.setBounds(layerInfo.featureInfo.bounds);
                            layer.setFeaturesModified(layerInfo.featureInfo.lastModified);
                        }
                        overallTimestamp = Math.max(overallTimestamp, layerInfo.featureInfo.lastModified);
                    }
                    this.setTimestamp(overallTimestamp || Date.now());
                    this.eventChannel.fireEvent("onSyncDatasetFinishAndFeatureViewModelUpdated", syncDatasetFinishRS);
                });
            }, this)

            .addListener("onUpdateRecommendation", (layerViewId: number) => {
                if (this.layersById[layerViewId]) {
                    es.api.layerInfo.getForLayerView(layerViewId).done((layerInfo) => {
                        this.setTimestamp(layerInfo.featureInfo.lastModified);
                    });
                }
            }, this);
    }

    private loadLayerInfo(layerViews: ILayerView[]){
        const request = [];
        for(let i = 0; i < layerViews.length; i++){
            const layerView = layerViews[i];
            request.push({layerViewId: layerView.id, layerMetaId: layerView.layerMetaId});
        }
        return es.api.layerInfo.getAll(request);
    }

    private calculateTimestamp(geoMap: GeoMap) {
        let t = geoMap ? geoMap.lastModified : 0;
        this.layers.forEach((layer: any) => {
            t = Math.max(t, layer.getLastModified()); //TODO WRONG - DOES NOT ACCOUNT FOR LAYER INFO
        });
        return t;
    }

    private fireOnFeatureViewUpdateEvent() {
        if (this.fireOnFeatureViewUpdateEventTimeout) {
            // if there is already a timeout, there's already a promise
            window.clearTimeout(this.fireOnFeatureViewUpdateEventTimeout);
        } else {
            this.fireOnFeatureViewUpdateEventPromise = new Promise<void>((resolve) => {
                this.fireOnFeatureViewUpdateEventPromiseResolve = resolve;
            });
        }

        this.fireOnFeatureViewUpdateEventTimeout = window.setTimeout(async () => {
            const resolve = this.fireOnFeatureViewUpdateEventPromiseResolve as () => void;

            this.fireOnFeatureViewUpdateEventTimeout = undefined;
            this.fireOnFeatureViewUpdateEventPromise = undefined;
            this.fireOnFeatureViewUpdateEventPromiseResolve = undefined;

            await this._eventChannel.fireEvent("onFeatureViewUpdate");
            resolve();
        }, 10);

        return this.fireOnFeatureViewUpdateEventPromise;
    }

    private async updateLayerOrdering() {
        const dfds = [];
        for (let i = this.layers.length; i-- > 0;) {
            const layer = this.layers[i];
            dfds.push(layer.updateLayerView({'ordering': i}));
        }

        await Promise.all(dfds);
    }

    private async onUpdateLayer(layer: any, isTransient: boolean, updateEventNames: string[]) {
        this.timestamp = Math.max(this.timestamp, layer.getLastModified());
        // Carefull if removing the updateEventNames from the arguments of the event
        // The Datasource Manager is expecting this property
        await this._eventChannel.fireEvent("onUpdateLayer", layer, isTransient, updateEventNames);
        await this._eventChannel.fireEvent("onUpdate", isTransient, updateEventNames);
        await this._eventChannel.fireEvent("onFeatureViewModelUpdateLayer", layer, isTransient);
        await this.fireOnFeatureViewUpdateEvent();
    }

    public async addLayerView(layerView: ILayerView, layerInfo: LayerInfo, isSuppressEvent: boolean = false) {
        const layerModel = new LayerViewModel(layerView, layerInfo);
        this.layersById[layerModel.getId()] = layerModel;
        layerModel.getEventChannel().addListener("onUpdate", this.onUpdateLayer, this);
        const index = layerView.ordering; // get the order index from the server
        this.layers.splice(index, 0, layerModel);
        await this.updateLayerOrdering();
        this.timestamp = Math.max(this.timestamp, layerModel.getLastModified());
        if (!isSuppressEvent) {
            await this._eventChannel.fireEvent("onAddLayer", layerModel, index);
            await this._eventChannel.fireEvent("onUpdate");
        }
        await this.fireOnFeatureViewUpdateEvent();
    };

    /**
     * Remove a layer and return it.
     * @param ids the ids of the layer
     * @return the layer if it existed and was removed, null otherwise
     */
    public removeLayer(...ids: number[]) {
        for(const id of ids){
            let index = this.indexOfLayer(id);
            if (index >= 0) {
                let layer = this.layersById[id];
                delete this.layersById[id];
                this.layers.splice(index, 1);
                this.timestamp = Date.now();
                layer.getEventChannel().removeListener("onUpdate", this.onUpdateLayer, this);
                this._eventChannel.fireEvent("onRemoveLayer", layer, index);
                this._eventChannel.fireEvent("onUpdate");
                layer.destroy();
            } else {
                return false;
            }
        }

        this.fireOnFeatureViewUpdateEvent();
        return true;
    }

    /**
     * Get the index of a layer
     * @param layerViewId the id of the layer
     * @return the index of the layer, or -1 if there was no such layer
     */
    public indexOfLayer(layerViewId: number) {
        return this.layers.findIndex(l => l.getId() === layerViewId);
    }

    /**
     * Get the number of layers
     * @return
     */
    public getNumLayers() {
        return this.layers.length;
    };

    /**
     * Get a list of all layerViewIds in their current order. Throw an error if a layerView did not have a valid id.
     * @return layerViewids
     */
    public getLayerViewIds() {
        const ret: number[] = [];
        this.layers.forEach((layer: any) => {
            const id = layer.getId();
            if(id <= 0){
                throw new Error("LayerView exists without valid id ("+id+") - calling getLayerViewIds "+
                    "with virtual layers from layerMeta does not make sense.");
            }
            ret.push(id);
        });
        return ret;
    };

    /**
     * Get a layer based on its id
     * @param layerViewId the id of the layer
     * @return the layer or null if there was no such layer
     */
    public getLayerById(layerViewId: number) {
        if (!this.layersById[layerViewId]){
            throw new Error("Map does not contain layerView id " + layerViewId);
        }
        return this.layersById[layerViewId];
    };

    public getLayerByLayerMetaId(layerMetaId: number) {
        return this.layers.find(l => l.getLayerMetaId() === layerMetaId);
    };

    /**
     * Get a layer based on its index
     * @param index the index of the layer
     * @return the layer or null if there was no such layer
     */
    public getLayerByIndex(index: number) {
        return this.layers[index];
    };

    /**
     * Get layers by type or all layers if no param passed
     * @param layerType
     * @returns {*[]}
     */
    public getLayersByType(layerType: string): any[] {
        return this.layers.filter(layer => layer.getType() === layerType || layerType === undefined);
    }

    /**
     * Set the index of the layer with the id given
     * @param id the id of the layer
     * @param index new index for the layer
     * @return true if the layer existed and was moved, false otherwise
     */
    public setIndex(id: number, index: number) {
        const currentIndex = this.indexOfLayer(id);

        if (currentIndex < 0) {
            return false;
        }

        if (currentIndex == index) {
            return true; // the layer is already on the index, all good
        }

        this.layers.splice(currentIndex, 1);
        this.layers.splice(index, 0, this.layersById[id]);
        this.updateLayerOrdering();
        this.eventChannel.fireEvent("onUpdateLayerIndex", id, index);
        this.eventChannel.fireEvent("onUpdate");
        this.fireOnFeatureViewUpdateEvent();
        return true;
    }

    /**
     * List all layers
     * @return an array of all layer model objects
     */
    public listLayers(): LayerViewModel[] {
        return this.layers.slice();
    }

    /**
     * Convert this model object to a request
     * @param baseMap
     * @param explicitCriteria {optional} flag indicating that layers should be returned explicitly along with their criteria
     * @param overrideSortOrder {optional} an override of the sort order that is stored with the layer view
     */
    public getRequest(baseMap: AzureBaseMap, explicitCriteria?: boolean, overrideSortOrder?: any) {

        const isVisibleRes = (layer: any) => {
            const resolution = baseMap.getResolution();
            return (layer.getMinResolution() == 0 && layer.getMaxResolution() == 0) ||
                (resolution >= layer.getMinResolution() &&
                 resolution <= layer.getMaxResolution());
        };

        const getLayersRequest = (explicitCriteria: boolean = false) => {

            let request: any = {
                version: this.timestamp,
                layers: [],
                geoMapId: this._geoMapId
            };

            const layers = [];
            let t = 0;
            for (let i = 0; i < this.layers.length; i++) {
                if (this.layers[i].isEnabled() && isVisibleRes(this.layers[i]) ){
                    t = Math.max(t, this.layers[i].getLastModified(), this.layers[i].getFeaturesModified());
                    layers.push(this.layers[i]);
                }
            }
            if (t) {
                request.version = t;
            }


            const dfds = layers.map(function(layer) {
                if (layer.isDestroyed()) {
                    return es.util.deferred.resolved(null);
                }

                const styleSettings = layer.getStyleSettingsObjRef();
                if (styleSettings.$type != 'PointStyleSettings' || !styleSettings.colorThematicSettings || !styleSettings.colorThematicSettings.enabled) {
                    return layer.getRequest(explicitCriteria, overrideSortOrder);
                }

                // If we have a Point dateset with color thematics, ranges can be hidden, we must apply it if it is for select, so it won't select it
                return es.map.vector.style.buildStyler(layer.getLayerView()).then(function(styler: any) {
                    if (layer.isDestroyed()) {
                        return es.util.deferred.resolved(null);
                    }

                    const request = layer.getRequest(explicitCriteria, overrideSortOrder);

                    const stylerHideCriteria = es.map.vector.style.getHideFeaturesCriteria(styler);
                    if (stylerHideCriteria) {
                        request.criteria = es.util.criteria.and(request.criteria, stylerHideCriteria);
                    }

                    return request;
                });
            });

            return $.when.apply($, dfds).then(function() {
                const layerRequests =  Array.prototype.slice.call(arguments).filter(req => !!req);
                request.layers = layerRequests;
                return request;
            });
        }

        if (explicitCriteria){
            return getLayersRequest(explicitCriteria);
        }

        for (let i = this.layers.length; i-- > 0; ) {
            const layer = this.layers[i];
            if (layer.needsExplicitRequest(overrideSortOrder)) {
                return getLayersRequest(explicitCriteria);
            }
        }

        const request = {
            version: this.timestamp,
            geoMapId: this._geoMapId
        };

        return es.util.deferred.resolved(request);
    }

    /**
     * Set the current timestamp for this model
     * @param newTimestamp most recent last modified date
     */
    public setTimestamp(newTimestamp: number){
        if(newTimestamp > this.timestamp) {
            this.timestamp = newTimestamp;
        } else if(newTimestamp == this.timestamp) {
            this.timestamp = Date.now();
        } else {
            if(es.isDeveloper){
                console.warn("featureViewModel : setTimestamp : newTimestamp is older than the current timestamp ", this.timestamp, new Date(this.timestamp), newTimestamp, new Date(newTimestamp), (this.timestamp - newTimestamp));
            }
            this.timestamp = Date.now();
        }
        this.eventChannel.fireEvent("onUpdate");
        this.fireOnFeatureViewUpdateEvent();
    }

    get geoMapId(): number {
        return this._geoMapId;
    }

    get onReady(): JQuery.Deferred<FeatureViewModel> {
        return this._onReady;
    }

    get eventChannel(): any {
        return this._eventChannel;
    }

    public isLeaderLineLabellingEnabledOnAnyLayer(){
        return this.listLayers()
            .map(layer => layer.getLayerView())
            .filter(layerView => layerView.styleSettings.labelSettings.enabled && layerView?.styleSettings?.labelSettings?.labelling === "LEADER_LINES")
            .length > 0;
    }
}
